import 'swiper/swiper.scss';
import 'swiper/modules/pagination/pagination.scss';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import I18n from 'i18n-js';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';

import { readTutorial } from '@lootboy/lib/actions/UserActions';
import { useTutorial } from '@lootboy/lib/slices/tutorials';
import { NOOP } from '@lootboy/lib/utils';

import ButtonedPopupTemplate from '~/components/ButtonedPopupTemplate';
import PopupButtoned from '~/components/PopupButtoned';

import style from './TutorialView.module.scss';

function TutorialView() {
  const { id } = useParams();
  const history = useHistory();
  const tutorial = useTutorial(id);
  const { screens } = tutorial;
  const dispatch = useDispatch();
  const pagination = {
    clickable: true,
  };
  const swiperRef = useRef(undefined);

  const onClose = useCallback(() => history.goBack(), [history]);

  const initialCancelButtonProps = useMemo(
    () => ({
      onCloseButtonClick: onClose,
      cancel: 'common_close',
    }),
    [onClose],
  );

  const initialSubmitButtonProps = useMemo(
    () => ({
      onSubmit: () => swiperRef?.current?.swiper?.slideNext(),
      okay: 'common_continue',
    }),
    [],
  );

  const [{ onCloseButtonClick, cancel }, setCloseButtonsProps] = useState(
    initialCancelButtonProps,
  );

  const [{ onSubmit, okay }, setSubmitButtonProps] = useState(
    initialSubmitButtonProps,
  );

  const markTutorialAsRead = () =>
    dispatch(readTutorial(tutorial._id)).catch(NOOP);

  const onActiveIndexChange = ({ realIndex }) => {
    if (realIndex > 0) {
      setCloseButtonsProps({
        onCloseButtonClick: () => swiperRef?.current?.swiper?.slidePrev(),
        cancel: 'common_back',
      });
    } else {
      setCloseButtonsProps(initialCancelButtonProps);
    }

    if (realIndex === screens.length - 1) {
      setSubmitButtonProps({
        onSubmit: onClose,
        okay: 'common_close',
      });
    } else {
      setSubmitButtonProps(initialSubmitButtonProps);
    }
  };

  return (
    <PopupButtoned
      onCloseButtonClick={onCloseButtonClick}
      onClose={onClose}
      onSubmit={onSubmit}
      cancel={I18n.t(cancel)}
      okay={I18n.t(okay)}
      contentClassName={style.content}
      buttonsClassName={style.buttonsWrapper}
    >
      <Swiper
        modules={[Pagination]}
        ref={swiperRef}
        pagination={pagination}
        className={style.swiper}
        autoHeight
        onReachEnd={markTutorialAsRead}
        onActiveIndexChange={onActiveIndexChange}
      >
        {screens.map(({ image, text, title, _id }) => (
          <SwiperSlide key={_id}>
            <ButtonedPopupTemplate
              path={image}
              headline={title}
              message={text}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </PopupButtoned>
  );
}

export default TutorialView;
